// @ts-strict-ignore
import { useMutation } from "@apollo/client";
import ActionDialog from "@dashboard/components/ActionDialog";
import { Select } from "@dashboard/components/Select";
import useNotifier from "@dashboard/hooks/useNotifier";
import { Accordion, AccordionDetails, AccordionSummary, Typography } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Box, Input, Option, Textarea } from "@saleor/macaw-ui-next";
import React, { useState } from "react";

import CmsToggle from "../../cms/components/CmsToggle";
import { SDUI_THEME_CREATE_MUTATION, SDUI_THEME_UPDATE_MUTATION } from "../sduiMutations";
import { SDUI_THEMES_QUERY, STOREFRONT_LAYOUT_QUERY } from "../sduiQueries";
import { SDUIAnimationEffect, SDUITheme } from "../types";

interface ThemeDialogProps {
  open: boolean;
  theme?: SDUITheme | null;
  onClose: () => void;
}

const ANIMATION_EFFECT_OPTIONS: Option[] = [
  { label: "None (Standard Clean)", value: "NONE" },
  { label: "🪔 Festive Diyas & Sparkles (Diwali / Utsav)", value: "FESTIVAL_DIYAS" },
  { label: "🎉 Celebration Confetti (New Year / Milestones)", value: "CONFETTI" },
  { label: "🍃 Monsoon / Harvest Falling Leaves", value: "FALLING_LEAVES" },
  { label: "✨ Golden Shimmer & Glow (Royal Festive)", value: "GOLDEN_SPARKLES" },
  { label: "⚡ 10-Min Delivery Speed Pulse", value: "PULSE_BADGE" },
  { label: "💫 Shimmer Light Sweep (Blinkit Style)", value: "SHIMMER_SWEEP" },
  { label: "🌟 Spotlight Pulsing Border (Zepto Style)", value: "PULSE_BORDER" },
  { label: "🏷️ Attention Wiggle & Bounce (Instamart)", value: "BOUNCE_WIGGLE" },
];

export const ThemeDialog: React.FC<ThemeDialogProps> = ({ open, theme, onClose }) => {
  const notify = useNotifier();
  const isEdit = !!theme;

  const [name, setName] = useState(theme?.name || "");
  const [label, setLabel] = useState(theme?.label || "");
  const [priority, setPriority] = useState(theme ? theme.priority.toString() : "0");
  const [animationEffect, setAnimationEffect] = useState<SDUIAnimationEffect>(
    theme?.animationEffect || "NONE"
  );

  // Visual Styling Tokens
  const [primaryColor, setPrimaryColor] = useState("#15803D");
  const [accentColor, setAccentColor] = useState("#F59E0B");
  const [backgroundColor, setBackgroundColor] = useState("#F8FAF6");
  const [surfaceColor, setSurfaceColor] = useState("#FFFFFF");
  const [textColor, setTextColor] = useState("#1F2937");

  // Quick-Commerce Floating Elements
  const [enableStickyCart, setEnableStickyCart] = useState(true);
  const [cartBadgeText, setCartBadgeText] = useState("10-MIN EXPRESS");
  const [enableDeliveryBar, setEnableDeliveryBar] = useState(true);
  const [deliveryEtaText, setDeliveryEtaText] = useState("Delivering in 11 mins to Indiranagar");

  // Advanced Raw JSON override
  const [rawThemeJson, setRawThemeJson] = useState("");
  const [isPublished, setIsPublished] = useState(theme ? theme.isPublished : true);
  const [heroFile, setHeroFile] = useState<File | null>(null);
  const [splashFile, setSplashFile] = useState<File | null>(null);

  React.useEffect(() => {
    if (theme) {
      setName(theme.name || "");
      setLabel(theme.label || "");
      setPriority(theme.priority !== undefined && theme.priority !== null ? theme.priority.toString() : "0");
      setAnimationEffect(theme.animationEffect || "NONE");

      let parsedData: any = {};
      try {
        parsedData = typeof theme.themeData === "string" ? JSON.parse(theme.themeData) : (theme.themeData || {});
      } catch {
        parsedData = {};
      }
      setPrimaryColor(parsedData.primaryColor || "#15803D");
      setAccentColor(parsedData.accentColor || parsedData.secondaryColor || "#F59E0B");
      setBackgroundColor(parsedData.backgroundColor || "#F8FAF6");
      setSurfaceColor(parsedData.surfaceColor || "#FFFFFF");
      setTextColor(parsedData.textColor || "#1F2937");
      setRawThemeJson(JSON.stringify(parsedData, null, 2));

      let parsedFloating: any = {};
      try {
        parsedFloating =
          typeof theme.floatingElementsConfig === "string"
            ? JSON.parse(theme.floatingElementsConfig)
            : (theme.floatingElementsConfig || {});
      } catch {
        parsedFloating = {};
      }
      setEnableStickyCart(parsedFloating.enable_sticky_cart ?? true);
      setCartBadgeText(parsedFloating.cart_badge_text || "10-MIN EXPRESS");
      setEnableDeliveryBar(parsedFloating.enable_delivery_bar ?? true);
      setDeliveryEtaText(parsedFloating.delivery_eta_text || "Delivering in 11 mins to Indiranagar");

      setIsPublished(theme.isPublished ?? true);
      setHeroFile(null);
      setSplashFile(null);
    } else {
      setName("");
      setLabel("");
      setPriority("0");
      setAnimationEffect("NONE");
      setPrimaryColor("#15803D");
      setAccentColor("#F59E0B");
      setBackgroundColor("#F8FAF6");
      setSurfaceColor("#FFFFFF");
      setTextColor("#1F2937");
      setRawThemeJson(
        JSON.stringify(
          {
            primaryColor: "#15803D",
            secondaryColor: "#F59E0B",
            accentColor: "#10B981",
            backgroundColor: "#F8FAF6",
            surfaceColor: "#FFFFFF",
            textColor: "#1F2937",
            borderRadius: 12,
            fontFamily: "Inter, sans-serif",
          },
          null,
          2
        )
      );
      setEnableStickyCart(true);
      setCartBadgeText("10-MIN EXPRESS");
      setEnableDeliveryBar(true);
      setDeliveryEtaText("Delivering in 11 mins to Indiranagar");
      setIsPublished(true);
      setHeroFile(null);
      setSplashFile(null);
    }
  }, [theme, open]);

  const [createTheme, { loading: creating }] = useMutation(SDUI_THEME_CREATE_MUTATION, {
    refetchQueries: [
      { query: SDUI_THEMES_QUERY },
      { query: STOREFRONT_LAYOUT_QUERY, variables: { channel: "kisan-basket", platform: "APP" } },
      { query: STOREFRONT_LAYOUT_QUERY, variables: { channel: "kisan-basket", platform: "WEB" } },
    ],
    onCompleted: data => {
      if (data.sduiThemeCreate?.errors?.length) {
        notify({ status: "error", text: data.sduiThemeCreate.errors[0].message });
      } else {
        notify({ status: "success", text: "Theme created successfully" });
        onClose();
      }
    },
  });

  const [updateTheme, { loading: updating }] = useMutation(SDUI_THEME_UPDATE_MUTATION, {
    refetchQueries: [
      { query: SDUI_THEMES_QUERY },
      { query: STOREFRONT_LAYOUT_QUERY, variables: { channel: "kisan-basket", platform: "APP" } },
      { query: STOREFRONT_LAYOUT_QUERY, variables: { channel: "kisan-basket", platform: "WEB" } },
    ],
    onCompleted: data => {
      if (data.sduiThemeUpdate?.errors?.length) {
        notify({ status: "error", text: data.sduiThemeUpdate.errors[0].message });
      } else {
        notify({ status: "success", text: "Theme updated successfully" });
        onClose();
      }
    },
  });

  const handleSubmit = () => {
    if (!name || !label) {
      notify({ status: "error", text: "Theme name and label are required" });
      return;
    }

    let stylingTokens: any = {};
    try {
      stylingTokens = rawThemeJson ? JSON.parse(rawThemeJson) : {};
    } catch {
      notify({ status: "error", text: "Invalid JSON in advanced styling tokens" });
      return;
    }

    stylingTokens.primaryColor = primaryColor;
    stylingTokens.accentColor = accentColor;
    stylingTokens.secondaryColor = accentColor;
    stylingTokens.backgroundColor = backgroundColor;
    stylingTokens.surfaceColor = surfaceColor;
    stylingTokens.textColor = textColor;

    const floatingConfig = {
      enable_sticky_cart: enableStickyCart,
      cart_badge_text: cartBadgeText,
      enable_delivery_bar: enableDeliveryBar,
      delivery_eta_text: deliveryEtaText,
    };

    const input: any = {
      name,
      label,
      priority: parseInt(priority, 10) || 0,
      animationEffect,
      themeData: JSON.stringify(stylingTokens),
      floatingElementsConfig: JSON.stringify(floatingConfig),
      isPublished,
    };
    if (heroFile) input.heroImage = heroFile;
    if (splashFile) input.splashImage = splashFile;

    if (isEdit) {
      updateTheme({ variables: { id: theme.id, input } });
    } else {
      createTheme({ variables: { input } });
    }
  };

  return (
    <ActionDialog
      open={open}
      size="md"
      title={isEdit ? "Edit 2026 Visual Theme & Atmosphere" : "Create Visual Theme"}
      confirmButtonState={creating || updating ? "loading" : "default"}
      onClose={onClose}
      onConfirm={handleSubmit}
    >
      <Box display="flex" flexDirection="column" gap={4} width="100%">
        {/* Core Identifiers */}
        <Box style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Input
            label="Theme Key (System ID)"
            value={name}
            onChange={e => setName(e.target.value)}
            disabled={isEdit}
            placeholder="e.g. festive_harvest_gold, monsoon_green"
            helperText="Unique system identifier for the theme"
            width="100%"
            required
          />
          <Input
            label="Priority (Higher = Active Override)"
            type="number"
            value={priority}
            onChange={e => setPriority(e.target.value)}
            placeholder="0"
            helperText="Higher numbers take precedence"
            width="100%"
          />
        </Box>

        <Input
          label="Display Label"
          value={label}
          onChange={e => setLabel(e.target.value)}
          placeholder="e.g. Festive Harvest Gold — Diwali & Utsav Special"
          helperText="Public-facing theme name and tagline"
          width="100%"
          required
        />

        {/* Atmospheric Animation Effect */}
        <Select
          label="Atmospheric Screen Animation (2026 Quick-Commerce)"
          name="animationEffect"
          value={animationEffect}
          options={ANIMATION_EFFECT_OPTIONS}
          onChange={({ target }) => setAnimationEffect(target.value as SDUIAnimationEffect)}
        />

        {/* Visual Color Palette Picker */}
        <Box
          style={{
            backgroundColor: "#F9FAFB",
            border: "1px solid #E5E7EB",
            borderRadius: 10,
            padding: "14px 16px",
          }}
          display="flex"
          flexDirection="column"
          gap={3}
        >
          <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#111827" }}>
            🎨 Quick-Commerce Color Palette
          </Typography>

          <Box style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>
                Primary Brand Color
              </label>
              <Box display="flex" alignItems="center" gap={2}>
                <input
                  type="color"
                  value={primaryColor}
                  onChange={e => setPrimaryColor(e.target.value)}
                  style={{ width: 38, height: 38, borderRadius: 8, border: "1px solid #D1D5DB", cursor: "pointer", padding: 2 }}
                />
                <Input size="small" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} width="100%" />
              </Box>
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>
                Accent / Gold Color
              </label>
              <Box display="flex" alignItems="center" gap={2}>
                <input
                  type="color"
                  value={accentColor}
                  onChange={e => setAccentColor(e.target.value)}
                  style={{ width: 38, height: 38, borderRadius: 8, border: "1px solid #D1D5DB", cursor: "pointer", padding: 2 }}
                />
                <Input size="small" value={accentColor} onChange={e => setAccentColor(e.target.value)} width="100%" />
              </Box>
            </div>

            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 }}>
                Background Canvas
              </label>
              <Box display="flex" alignItems="center" gap={2}>
                <input
                  type="color"
                  value={backgroundColor}
                  onChange={e => setBackgroundColor(e.target.value)}
                  style={{ width: 38, height: 38, borderRadius: 8, border: "1px solid #D1D5DB", cursor: "pointer", padding: 2 }}
                />
                <Input size="small" value={backgroundColor} onChange={e => setBackgroundColor(e.target.value)} width="100%" />
              </Box>
            </div>
          </Box>
        </Box>

        {/* Floating Quick Commerce Elements */}
        <Box
          style={{
            backgroundColor: "#F0FDF4",
            border: "1px solid #BBF7D0",
            borderRadius: 12,
            padding: "16px 18px",
          }}
          display="flex"
          flexDirection="column"
          gap={4}
        >
          <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#166534" }}>
            ⚡ Floating Quick-Commerce Controls
          </Typography>

          {/* Sticky Cart Row */}
          <Box
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              alignItems: "center",
            }}
          >
            <Box
              style={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #DCFCE7",
                borderRadius: 8,
                padding: "8px 12px",
              }}
            >
              <CmsToggle
                label="Sticky Floating Cart Pill"
                description="Floating bottom cart drawer trigger"
                checked={enableStickyCart}
                onChange={setEnableStickyCart}
              />
            </Box>
            <Input
              label="Cart Badge Text"
              value={cartBadgeText}
              onChange={e => setCartBadgeText(e.target.value)}
              placeholder="e.g. 10-MIN EXPRESS"
              helperText="Badge shown on floating sticky cart pill"
              width="100%"
            />
          </Box>

          {/* Delivery Bar Row */}
          <Box
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              alignItems: "center",
            }}
          >
            <Box
              style={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #DCFCE7",
                borderRadius: 8,
                padding: "8px 12px",
              }}
            >
              <CmsToggle
                label="Top Delivery ETA Bar"
                description="Real-time delivery status header"
                checked={enableDeliveryBar}
                onChange={setEnableDeliveryBar}
              />
            </Box>
            <Input
              label="Delivery ETA Text"
              value={deliveryEtaText}
              onChange={e => setDeliveryEtaText(e.target.value)}
              placeholder="e.g. Delivering in 11 mins to Indiranagar"
              helperText="Live promise header displayed under search bar"
              width="100%"
            />
          </Box>
        </Box>

        {/* Optional Graphic Uploads */}
        <Box style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Box display="flex" flexDirection="column" gap={1}>
            <label style={{ fontSize: "0.85rem", color: "#666", fontWeight: 500 }}>
              Hero Header Graphic (Optional)
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={e => setHeroFile(e.target.files?.[0] || null)}
            />
          </Box>
          <Box display="flex" flexDirection="column" gap={1}>
            <label style={{ fontSize: "0.85rem", color: "#666", fontWeight: 500 }}>
              Mobile Splash Graphic (Optional)
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={e => setSplashFile(e.target.files?.[0] || null)}
            />
          </Box>
        </Box>

        {/* Collapsible Advanced JSON Override */}
        <Accordion style={{ border: "1px solid #E5E7EB", borderRadius: 8, boxShadow: "none" }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2" style={{ fontWeight: 600, color: "#4B5563" }}>
              🛠️ Advanced Styling Tokens JSON (Overrides)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Textarea
              label="Raw JSON Styling Tokens"
              value={rawThemeJson}
              onChange={e => setRawThemeJson(e.target.value)}
              rows={4}
              width="100%"
              helperText="Define tokens: primaryColor, secondaryColor, accentColor, surfaceColor, borderRadius, etc."
            />
          </AccordionDetails>
        </Accordion>

        <CmsToggle
          label="Published & Eligible for Storefront"
          description="Make this theme active and visible on mobile & web clients"
          checked={isPublished}
          onChange={setIsPublished}
        />
      </Box>
    </ActionDialog>
  );
};

export default ThemeDialog;
